import {
  brandMatchesCompetitor as matchBrandToCompetitor,
  type CompetitorMatchBrand,
} from './brand-matching';
import { resolveAiMentions } from './visibility-metrics';
import type {
  CompetitorInsight,
  DomainFilterValue,
  ProjectVisibilitySummary,
  ProjectVisibilityTopBrand,
  VisibilityTrendBrandSeries,
  VisibilityTrendResponse,
} from '../types';

export interface DomainBrandLike {
  knownBrandId?: string | null;
  competitorId?: string | null;
  canonicalName: string;
  domain?: string | null;
  relationship?: string;
}

export interface DomainScopedKpis {
  visibilityScore: number | null;
  avgPosition: number | null;
  aiMentions: number | null;
  /** @deprecated Use aiMentions */
  totalMentions: number | null;
}

export function brandKey(brand: DomainBrandLike): string {
  return brand.knownBrandId ?? brand.canonicalName.trim().toLowerCase();
}

export type { CompetitorMatchBrand } from './brand-matching';

export function brandMatchesCompetitor(
  brand: CompetitorMatchBrand,
  competitor: CompetitorInsight,
): boolean {
  return matchBrandToCompetitor(brand, competitor);
}

export function findBrandForDomainFilter<T extends DomainBrandLike>(
  brands: T[],
  filter: DomainFilterValue,
  competitors: CompetitorInsight[],
): T | null {
  if (filter === 'all') return null;
  if (filter === 'you') {
    return brands.find((brand) => brand.relationship === 'you') ?? null;
  }

  const competitor = competitors.find((item) => item.id === filter);
  if (!competitor) return null;

  return brands.find((brand) => brandMatchesCompetitor(brand, competitor)) ?? null;
}

export function resolveVisibilityTopBrand(
  summary: Pick<ProjectVisibilitySummary, 'topBrands' | 'topCompetitors'>,
  filter: DomainFilterValue,
  competitors: CompetitorInsight[],
): ProjectVisibilityTopBrand | null {
  if (filter === 'all') return null;

  const pool = [...summary.topBrands, ...summary.topCompetitors];
  const seen = new Set<string>();
  const uniqueBrands = pool.filter((brand) => {
    const key = brandKey(brand);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return findBrandForDomainFilter(uniqueBrands, filter, competitors);
}

export function getDomainScopedSummaryKpis(
  summary: ProjectVisibilitySummary,
  filter: DomainFilterValue,
  competitors: CompetitorInsight[],
): DomainScopedKpis {
  if (filter === 'all') {
    return {
      visibilityScore: summary.summary.visibilityScore,
      avgPosition: summary.summary.avgPosition,
      aiMentions: summary.summary.aiMentions ?? null,
      totalMentions: summary.summary.aiMentions ?? null,
    };
  }

  const brand = resolveVisibilityTopBrand(summary, filter, competitors);
  if (!brand) {
    return { visibilityScore: null, avgPosition: null, aiMentions: null, totalMentions: null };
  }

  const aiMentions = resolveAiMentions(brand);
  return {
    visibilityScore: brand.visibilityScore > 0 ? brand.visibilityScore : null,
    avgPosition: brand.avgPosition,
    aiMentions,
    totalMentions: aiMentions,
  };
}

export function trendSeriesMatchesFilter(
  series: VisibilityTrendBrandSeries,
  filter: DomainFilterValue,
  competitors: CompetitorInsight[],
): boolean {
  if (filter === 'all') return true;
  if (filter === 'you') return series.relationship === 'own_brand';

  const competitor = competitors.find((item) => item.id === filter);
  if (!competitor) return false;

  const seriesBrand: DomainBrandLike = {
    knownBrandId: series.brandId,
    canonicalName: series.name,
    domain: series.domain,
  };
  return brandMatchesCompetitor(seriesBrand, competitor);
}

export function filterTrendByDomain(
  trend: VisibilityTrendResponse | null,
  filter: DomainFilterValue,
  competitors: CompetitorInsight[],
): VisibilityTrendResponse | null {
  if (!trend || filter === 'all') return trend;

  const brands = trend.brands.filter((series) =>
    trendSeriesMatchesFilter(series, filter, competitors),
  );
  if (!brands.length) return { ...trend, brands: [] };

  return { ...trend, brands };
}

export function getTrendBrandKeysForFilter(
  summary: Pick<ProjectVisibilitySummary, 'topBrands' | 'topCompetitors'>,
  filter: DomainFilterValue,
  competitors: CompetitorInsight[],
): string[] | undefined {
  if (filter === 'all') {
    return summary.topBrands.slice(0, 5).map((brand) => brandKey(brand));
  }

  const brand = resolveVisibilityTopBrand(summary, filter, competitors);
  if (!brand) {
    if (filter === 'you') {
      const own = summary.topBrands.find((item) => item.relationship === 'you');
      return own ? [brandKey(own)] : undefined;
    }
    const competitor = competitors.find((item) => item.id === filter);
    if (competitor) {
      return [competitor.brandName.trim().toLowerCase()];
    }
    return undefined;
  }

  return [brandKey(brand)];
}

export function competitorRowMatchesFilter(
  row: { brandId?: string | null; name: string; domain?: string | null; competitorId?: string | null },
  filter: DomainFilterValue,
  competitors: CompetitorInsight[],
): boolean {
  if (filter === 'all') return false;
  if (filter === 'you') return false;

  const competitor = competitors.find((item) => item.id === filter);
  if (!competitor) return false;

  return brandMatchesCompetitor(
    {
      knownBrandId: row.brandId,
      canonicalName: row.name,
      domain: row.domain,
      competitorId: row.competitorId,
    },
    competitor,
  );
}

/** Comparison widgets: own brand always stays; other brands only when they are the selected competitor. */
export function brandInDomainFilter(
  row: { brandId?: string | null; name: string; domain?: string | null; isOwn: boolean },
  filter: DomainFilterValue,
  competitors: CompetitorInsight[],
): boolean {
  return filter === 'all' || row.isOwn || competitorRowMatchesFilter(row, filter, competitors);
}

/** Dashboard subject: own brand, or the competitor picked in the domain filter. */
export function isDomainFocusBrand(
  row: { brandId?: string | null; name: string; domain?: string | null; isOwn: boolean },
  filter: DomainFilterValue,
  competitors: CompetitorInsight[],
): boolean {
  if (filter === 'all' || filter === 'you') return row.isOwn;
  return competitorRowMatchesFilter(row, filter, competitors);
}
